import { useAuthStore } from '@/auth/auth-store';
import { useDoctorsList, useProfessionalDetails } from '@/settings/services';

/**
 * Provider-picking data for the consent fill flow (PROD-1051).
 *
 * When a consent template has a `provider-signature` field and the signed-in
 * user is NOT a doctor (receptionist / nurse / admin filling the form), they
 * must SELECT which provider's stored signature to apply — the filler isn't
 * necessarily the treating provider. These two hooks back that picker:
 *   - `usePickableProviders()` — the org's doctors, mapped to {id, name, speciality}.
 *   - `useProviderSignatureUrl(doctorSetupId)` — the selected provider's
 *     `doctorSignatureUrl` (+ a loading flag) for whoever was picked.
 *
 * Cross-domain note: these wrap the settings GET hooks so the consent popup +
 * picker import only from `@/consent/services` (the settings coupling lives here,
 * mirroring `useProviderConsentMarks`). Both underlying queries are
 * `enabled`-guarded on a truthy id, so a null/absent selection is a no-op.
 */

export interface PickableProvider {
  doctorSetupId: string;
  name: string;
  speciality: string | null;
}

export function usePickableProviders(): {
  providers: PickableProvider[];
  isLoading: boolean;
} {
  const orgId = useAuthStore((s) => s.user?.organizationId ?? null);
  // Active doctors only (showArchived=false). Org-scoped list; the picker
  // filters client-side via SearchPicker.
  const { data = [], isLoading } = useDoctorsList(orgId, false);
  const providers = data
    .filter((d) => d.doctorSetupId)
    .map((d) => ({
      doctorSetupId: d.doctorSetupId as string,
      name: `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim() || 'Provider',
      speciality: d.speciality?.code?.displayName ?? null,
    }));
  return { providers, isLoading };
}

export function useProviderSignatureUrl(
  doctorSetupId: string | null,
): { signatureUrl: string | null; isLoading: boolean } {
  const { data, isLoading } = useProfessionalDetails(doctorSetupId);
  return {
    signatureUrl: data?.doctorSignatureUrl ?? null,
    isLoading: !!doctorSetupId && isLoading,
  };
}
